/**
 * Ponto de entrada do cliente — configuração Phaser conforme §4 da spec:
 * 256×384 retrato, pixelArt, centralizado, fundo preto.
 *
 * Não há mais carregamento de TTF aqui: o texto do jogo é bitmap font 1-bit
 * (`ui/text.ts`), pré-rasterizada por `npm run font`. Fonte de sistema no
 * caminho do desenho significa antialias, e antialias em 8px destrói a letra.
 */

import Phaser from 'phaser'
import { SCREEN } from '@pixelraid/core'
import { GameScene } from './scenes/GameScene'
import { LobbyScene } from './scenes/LobbyScene'
import { MenuScene } from './scenes/MenuScene'

/**
 * Zoom do canvas.
 *
 * Escala fracionária + `image-rendering: pixelated` reparte mal os pixels: com
 * 3,4036× (medido em 1920×1080) uma linha do glifo saía com 3 pixels e a de
 * baixo com 4, entortando a letra. Em fator inteiro cada pixel da fonte vira
 * um quadrado igual.
 *
 * O piso de 2× é deliberado: em tela pequena (celular de 390px, fator 1,52)
 * arredondar para 1× deixaria o jogo num quadradinho no meio da tela. Ali vale
 * mais preencher a tela do que casar a grade — e com a fonte já 1-bit, o custo
 * do fator quebrado é uma leve irregularidade, não o borrão de antes.
 */
function fitZoom(): number {
  const fit = Math.min(window.innerWidth / SCREEN.WIDTH, window.innerHeight / SCREEN.HEIGHT)
  return fit >= 2 ? Math.floor(fit) : fit
}

async function boot(): Promise<void> {
  const params = new URLSearchParams(location.search)
  const skipMenu = params.has('seed') || params.has('daily') || params.has('coop')

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    width: SCREEN.WIDTH,
    height: SCREEN.HEIGHT,
    backgroundColor: '#000000',
    pixelArt: true,
    roundPixels: true,
    scale: {
      // NONE + zoom manual: o modo FIT calcula o fator sozinho e sempre
      // fracionário, sem gancho para arredondá-lo.
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      zoom: fitZoom(),
    },
    // NENHUMA cena no config: o Phaser 4 processa todas as que estiverem
    // aqui (≠ P3). Registro manual com autoStart=false + start explícito.
    scene: [],
    callbacks: {
      postBoot: (booted) => {
        booted.scene.add('menu', MenuScene, false)
        booted.scene.add('game', GameScene, false)
        booted.scene.add('lobby', LobbyScene, false)
        booted.scene.start(skipMenu ? 'game' : 'menu')
      },
    },
  })

  // Com `Scale.NONE` o Phaser não recalcula nada ao redimensionar a janela:
  // o zoom é nosso, e refazê-lo aqui é o que mantém o fator inteiro depois
  // de maximizar, girar o celular ou abrir o devtools.
  window.addEventListener('resize', () => {
    game.scale.setZoom(fitZoom())
  })
}

void boot()
